import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { launchAgentWithWidget } from '@/features/chats';
import {
  getSpringboardTabByIdMerged,
  ToolGridV4,
  useSpringboardTabs,
} from '@/features/springboard';
import {
  SectionPageHeader,
  SectionPageHeaderBackButton,
  SectionPageHeaderContent,
  SectionPageHeaderDescription,
  SectionPageHeaderIcon,
  SectionPageHeaderTitle,
} from '@/shared/ui/layout/SectionPageHeader/SectionPageHeader';
import { ListPageBody } from '@/shared/ui/layout/ListPageLayout/ListPageLayout';
import { ROUTES } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';
import { useCurrentWorkspaceSlug } from '@/shared/hooks';
import { useTranslation } from 'react-i18next';
import { TOOL_BADGE_TINTS } from '@/shared/ui/ToolBadge';

// eslint-disable-next-line i18next/no-literal-string -- tool category id
const DELIVER_TAB_ID = 'deliver';

export const DeliverPage = () => {
  const { t } = useTranslation('tools');

  const navigate = useNavigate();
  const location = useLocation();

  // Back returns to the surface the user came from (Tools, etc.), falling back
  // to Home when the page is reached directly (deep link, refresh, no origin).
  const backTo = (location.state as { from?: string } | null)?.from ?? ROUTES.HOME;

  const workspaceSlug = useCurrentWorkspaceSlug();

  const { tabs: springboardTabs } = useSpringboardTabs();

  const selectedTab = useMemo(
    () => getSpringboardTabByIdMerged(springboardTabs, DELIVER_TAB_ID),
    [springboardTabs],
  );

  const launchAgent = useCallback(
    (agentId: string) => {
      launchAgentWithWidget(navigate, agentId, { backTo: location.pathname });
    },
    [navigate, location.pathname],
  );

  if (!selectedTab) {
    return null;
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="flex flex-col w-full gap-4 p-4">
        <StaggerItem index={0}>
          <SectionPageHeader>
            <SectionPageHeaderBackButton
              onClick={() => void navigate(backTo)}
              aria-label={t('toolsHub.deliverPage.back')}
            />
            <SectionPageHeaderIcon surfaceClassName={TOOL_BADGE_TINTS[selectedTab.id]}>
              <selectedTab.Icon className="size-5" />
            </SectionPageHeaderIcon>
            <SectionPageHeaderContent>
              <SectionPageHeaderTitle>{selectedTab.label}</SectionPageHeaderTitle>
              <StaggerItem index={1} variant="fade">
                <SectionPageHeaderDescription>{selectedTab.subtitle}</SectionPageHeaderDescription>
              </StaggerItem>
            </SectionPageHeaderContent>
          </SectionPageHeader>
        </StaggerItem>

        <StaggerItem index={2}>
          <ListPageBody className="gap-6 p-6">
            <h3 className="text-lg font-medium">{t('toolsHub.deliverPage.allTools')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <ToolGridV4
                selectedTab={selectedTab}
                showFlowDigest={false}
                workspaceSlug={workspaceSlug}
                onToolClick={launchAgent}
                className="contents"
              />
            </div>
          </ListPageBody>
        </StaggerItem>

        {/* MFINS-2828: GuidedLearningWorkflow temporarily hidden */}
        {/* <GuidedLearningWorkflow /> */}
      </div>
    </div>
  );
};
